import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Clock, CircleCheck, Flag } from "lucide-react";
import Button from "../../components/Button/Button";
import Card from "../../components/Card/Card";
import Loader from "../../components/Loader/Loader";
import Table from "../../components/Table/Table";
import StatCard from "../../components/StatCard/StatCard";
import StatusChip from "../../components/StatusChip/StatusChip";
import { formatDate, getWeekRange, hasPermission } from "../LessonPlanner/utils/lessonPlanUtils";
import ApproveDiaryModal from "./components/ApproveDiaryModal";
import SubmitDiaryModal from "./components/SubmitDiaryModal";
import PrincipalRemarksModal from "./components/PrincipalRemarksModal";
import DiaryFilterBar from "./components/DiaryFilterBar";
import { diaryLessonTableConstants } from "./DiaryLessonTableConst";
import { useTeacherDiary } from "./hooks/useTeacherDiary";
import { DIARY_PERMISSIONS, EXECUTION_FILTER_OPTIONS } from "./WeeklyDiaryConstant";
import "../LessonPlanner/LessonPlanner.css";
import withDiaryAccess from "./components/withDiaryAccess";
import "./WeeklyDiary.css";

const LIST_PATH = "/weekly-diary";
// Three summary cards share the row equally
const STAT_COL = "col-md-4 mb-4";

const WeeklyDiaryDetail = () => {
  const { teacherId, subjectId } = useParams();
  const [searchParams] = useSearchParams();
  const { state: rowInfo } = useLocation();
  const navigate = useNavigate();
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [isRemarksOpen, setIsRemarksOpen] = useState(false);
  // Teachers see their own diary: they can submit it, but not approve
  const isReviewer = useMemo(() => hasPermission(DIARY_PERMISSIONS.REVIEW), []);

  const { start, end } = useMemo(() => getWeekRange(searchParams.get("week_start") || new Date()), [searchParams]);
  const {
    diary,
    status,
    setStatus,
    search,
    setSearch,
    isLoading,
    isApproving,
    isSubmitting,
    approve,
    submit,
  } = useTeacherDiary({ teacherId, subjectId, weekStart: start });

  // Names from the API, falling back to what the overview row passed along
  const teacherName = diary?.teacherName || rowInfo?.teacherName || "";
  const subjectName = diary?.subjectName || rowInfo?.subjectName || "";
  const weekLabel = `${formatDate(start, "dd")}-${formatDate(end, "dd MMM''yy")}`;
  const isApproved = Boolean(diary?.isApproved);
  // Reviewers, or anyone the backend allows (can_approve); the approve API has the final say
  // and its error message is shown if it refuses
  const showApprove = Boolean(diary) && !isApproved && (isReviewer || Boolean(diary.canApprove));

  // Teacher can submit until the diary is submitted / approved
  const canSubmit = !showApprove && Boolean(diary) && !diary.isSubmitted && !isApproved;
  const isAwaitingApproval = Boolean(diary?.isSubmitted) && !isApproved;

  const submitApproval = async (remarks) => {
    const isSaved = await approve(remarks);
    if (isSaved) setIsApproveOpen(false);
  };

  const submitDiaryForApproval = async (reflection) => {
    const isSaved = await submit(reflection);
    if (isSaved) setIsSubmitOpen(false);
  };

  const back = () => navigate(`${LIST_PATH}`);

  if (!diary && isLoading) {
    return (
      <Card componentName="Weekly Diary" back={back}>
        <Loader />
      </Card>
    );
  }

  const summary = diary?.summary || {};
  const lessons = diary?.lessons || [];

  return (
    <Card componentName="Weekly Diary" back={back}>
      <div className="wd-detail">
        <div className="wd-detail__heading">
          <div>
            <h2>{teacherName}</h2>
            <p>Subject : {subjectName}</p>
          </div>
          {isAwaitingApproval && <StatusChip tone="warning">Submitted · awaiting approval</StatusChip>}
        </div>

        <div className="row">
          <StatCard value={summary.completed ?? 0} label="Completed" icon={CircleCheck} tone="success" colClassName={STAT_COL} />
          <StatCard value={summary.pending ?? 0} label="Pending" icon={Clock} tone="warning" colClassName={STAT_COL} />
          <StatCard value={summary.flagged ?? 0} label="Flagged" icon={Flag} tone="danger" colClassName={STAT_COL} />
        </div>

        {isApproved && (
          <div className="wd-approved-banner">
            <strong>Approved by Principal</strong>
            <button type="button" className="wd-link-button" onClick={() => setIsRemarksOpen(true)}>
              View Remarks
            </button>
          </div>
        )}

        <DiaryFilterBar
          statusOptions={EXECUTION_FILTER_OPTIONS}
          status={status}
          onStatusChange={setStatus}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search class or reflection"
        >
          <span className="lp-week-nav wd-week-label">
            {formatDate(start, "dd MMM")} - {formatDate(end, "dd MMM''yy")}
          </span>
        </DiaryFilterBar>

        {isLoading && <Loader />}

        <div className="evaluating-table lp-review-table">
          <Table
            cols={diaryLessonTableConstants(lessons)}
            data={lessons}
            hoverable
            striped={false}
            getRowClassName={(lesson) => (lesson.isFlagged ? "lp-row-flagged" : "")}
          />
        </div>

        <div className="card wd-reflection">
          <div className="card-body">
            <h3>Teacher's Weekly Reflection</h3>
            <div className="wd-reflection__box">
              {diary?.reflection || (
                <span className="text-muted">
                  {isReviewer
                    ? "The teacher has not submitted this week's reflection yet."
                    : "You haven't submitted this week's diary yet. Use Submit Diary to add your reflection."}
                </span>
              )}
            </div>
          </div>
        </div>

        {showApprove && (
          <div className="lp-form-footer">
            <Button
              variant="success"
              icon={<CircleCheck size={14} />}
              onClick={() => setIsApproveOpen(true)}
              disabled={isLoading}
            >
              Approve Diary
            </Button>
          </div>
        )}

        {canSubmit && (
          <div className="lp-form-footer">
            <Button onClick={() => setIsSubmitOpen(true)} disabled={isLoading}>
              Submit Diary
            </Button>
          </div>
        )}
      </div>

      <ApproveDiaryModal
        isOpen={isApproveOpen}
        teacherName={teacherName}
        weekLabel={weekLabel}
        isDiarySubmitted={Boolean(diary?.isSubmitted)}
        isSubmitting={isApproving}
        onClose={() => setIsApproveOpen(false)}
        onSubmit={submitApproval}
      />

      <PrincipalRemarksModal isOpen={isRemarksOpen} diary={diary} onClose={() => setIsRemarksOpen(false)} />

      <SubmitDiaryModal
        isOpen={isSubmitOpen}
        subjectName={subjectName}
        weekLabel={weekLabel}
        initialReflection={diary?.reflection}
        isSubmitting={isSubmitting}
        onClose={() => setIsSubmitOpen(false)}
        onSubmit={submitDiaryForApproval}
      />
    </Card>
  );
};

export default withDiaryAccess(WeeklyDiaryDetail);
